import _ from "lodash";
import me from "models/me";
import { getStore } from "store";
import { GTagGroup, GoogleAnalyticsEvent, GoogleItem, Cart, FormatItemsWithBulkInput } from "./types";
import * as events from "./events";
import ItemModel from "models/item";
import { IScope } from "reducers/scope";
import { getOrder } from "components/build-order";

const GA_BRAND_SCRIPT_CLASS = "ga-brand";
const gtagReg = /G-\w+/;
const gtmReg = /GTM-\w+/;

interface IGoogleTagsManagerService {
    b2bMeasurementIds: any;
    groups: typeof GTagGroup;
    config: (brandMeasurementId: string) => void;
    emit: (data: GoogleAnalyticsEvent) => void;
    formatItem: (
        item: typeof ItemModel,
        scope: IScope,
        affiliation?: string,
        item_list_id?: string,
        item_list_name?: string
    ) => GoogleItem;
    formatItems: (
        items: (typeof ItemModel)[],
        scope: IScope,
        affiliation?: string,
        item_list_id?: string,
        item_list_name?: string
    ) => GoogleItem[];
    formatItemsWithBulk: (input: FormatItemsWithBulkInput) => { items: GoogleItem[]; value: number };
    fetchItemBrandId: (item: typeof ItemModel) => string;
    fetchValueFromItems: (items: (typeof ItemModel)[], scope: IScope) => number;
    fetchPageTitle: (url: string) => string;
    fetchCart: (state: any) => Cart;
    allowPageView: (url: string) => boolean;
    events: typeof events;
}

export const gtm: IGoogleTagsManagerService = {
    b2bMeasurementIds: {
        production: "G-3186SBDTZY",
        staging: "G-973TXWXN01",
        sandbox1: "G-973TXWXN01",
        development: "G-973TXWXN01",
        test: "G-973TXWXN01",
    },
    events,
    groups: GTagGroup,
    config: (brandMeasurementId: string) => {
        const brandScripts = fetchExistingBrandScripts();
        const b2bMeasurementId = fetchB2BMeasurementId();
        teardown(brandScripts, brandMeasurementId);
        setup(brandMeasurementId, b2bMeasurementId);
    },
    emit: (payload: GoogleAnalyticsEvent) => {
        try {
            const { event, send_to = GTagGroup.Default, properties } = payload;
            const target =
                typeof send_to === "string" ? fetchTarget(send_to) : send_to.map((group) => fetchTarget(group));

            window.dataLayer = window.dataLayer || [];
            if (!window.gtag) {
                window.gtag = function () {
                    // eslint-disable-next-line prefer-rest-params
                    window.dataLayer.push(arguments);
                };
                window.gtag("js", new Date());
            }

            if (!event || !target || isDuplicateEvent(["event", event, { ...properties, send_to: target }])) {
                return;
            }

            if (window.dataLayer.length > 50) {
                resetDataLayer();
            }

            window.gtag("event", event, { ...properties, send_to: target });
        } catch (err) {
            console.log(err);
        }
    },
    formatItem: (
        item: typeof ItemModel,
        scope: IScope,
        affiliation?: string,
        item_list_id?: string,
        item_list_name?: string
    ): GoogleItem => {
        const formattedAffiliation = ["linesheet", "cart", "hotspot"].includes(affiliation) ? affiliation : "list";
        const basicAffiliation = window.location.href.includes("gallery")
            ? "gallery"
            : window.location.href.includes("list")
              ? "list"
              : undefined;
        return {
            item_id: gtm.fetchItemBrandId(item),
            item_name: item?.name,
            affiliation: affiliation ? formattedAffiliation : basicAffiliation,
            item_list_id,
            item_list_name,
            item_brand: gtm.fetchItemBrandId(item),
            coupon: item?.current?.promotions?.applied?.[0]?.name || undefined,
            item_category: item?.style_number,
            item_category2: item?.season,
            item_category3: item?.color,
            price: item?.formatPriceForAnalytics(scope),
            discount: item?.discount,
            quantity: item?.total_quantity || 1,
        };
    },
    formatItems: (
        items: (typeof ItemModel)[],
        scope: IScope,
        affiliation?: string,
        item_list_id?: string,
        item_list_name?: string
    ): GoogleItem[] => {
        return (
            items?.map((item) => {
                if (!item) {
                    return null;
                }
                return gtm.formatItem(item, scope, affiliation, item_list_id, item_list_name);
            }) || []
        );
    },
    formatItemsWithBulk: (input: FormatItemsWithBulkInput): { items: GoogleItem[]; value: number } => {
        const { items, props, list } = input;
        const { single, bulk } = items;
        const { scope, entities } = props;
        const { affiliation, item_list_id, item_list_name } = list;

        if (single) {
            return {
                items: [gtm.formatItem(single, scope, affiliation, item_list_id, item_list_name)],
                value: gtm.fetchValueFromItems([single], scope),
            };
        }
        if (bulk && bulk?.size) {
            const bulkItems = bulk.toArray();
            const fetchedItems = bulkItems?.map((id) => {
                const itemId = entities?.entries?.[id]?.item_id;
                const item = entities?.items?.[id] ? entities.items[id] : entities?.items?.[itemId];
                return item;
            });
            const value = gtm.fetchValueFromItems(fetchedItems, scope);

            return {
                items: gtm.formatItems(fetchedItems, scope, affiliation, item_list_id, item_list_name),
                value,
            };
        }
        return { items: [], value: 0 };
    },
    fetchItemBrandId: (item: typeof ItemModel): string => {
        return item?.brand_id || item?.__unique_key || `${item?.style_number}_${item?.color_code || item?.color}`;
    },
    fetchValueFromItems: (items: (typeof ItemModel)[], scope: IScope): number => {
        return items?.reduce((acc, item) => {
            return acc + (item?.formatPriceForAnalytics(scope) || 0);
        }, 0);
    },
    fetchPageTitle: (url: string): string => {
        const parsedUrl = new URL(url);
        const segments = parsedUrl.pathname.split("/");
        if (segments.length > 3 && segments.includes("brand")) {
            return segments[segments.indexOf("brand") + 2];
        }
        return "landing page";
    },
    allowPageView: (url: string): boolean => {
        const { scope } = getStore().getState();

        const isEntityView =
            url.split("/").length > 6 && ["gallery", "linesheet", "list", "page", "item"].some((p) => url.includes(p));
        const isRejectedPath = ["cart"].some((p) => url.includes(p)) || !sameSource(scope);
        return !isEntityView && !isRejectedPath;
    },
    fetchCart: (state: any): Cart => {
        const { cart, entities, scope } = state;
        const order = getOrder(cart);
        const allItems = entities?.items;
        const cartItems = [];
        const currentItems = Array.from(cart?.items || []).length ? Array.from(cart.items) : order?.line_items;

        currentItems.forEach((id) => {
            if (allItems?.[id]) {
                cartItems.push(allItems[id]);
            }
        });

        return {
            items: gtm.formatItems(cartItems, scope, "cart", cart?.result, "cart"),
            value: order?.total,
            currency: order?.currency_code,
        };
    },
};

function fetchAllExistingScripts(): NodeListOf<HTMLScriptElement | HTMLIFrameElement> {
    return document.querySelectorAll('script[src*="googletagmanager.com"], iframe[src*="googletagmanager.com"]');
}

function fetchExistingBrandScripts(): (HTMLScriptElement | HTMLIFrameElement)[] {
    const taggedBrandScripts = document.querySelectorAll(
        `script[src*="googletagmanager.com"].${GA_BRAND_SCRIPT_CLASS}, iframe[src*="googletagmanager.com"].${GA_BRAND_SCRIPT_CLASS}`
    );
    const brandMeasurementIDs = new Set();
    // sometimes a brand script is loaded again without the identifying class
    taggedBrandScripts.forEach((script) => {
        const src = script.getAttribute("src") || "";
        const measurementId = src.match(gtagReg) ? src.match(gtagReg)[0] : null;
        if (measurementId) {
            brandMeasurementIDs.add(measurementId);
        }
    });
    const allScripts = fetchAllExistingScripts();
    const allBrandScripts = Array.from(allScripts).filter((script) => {
        const src = script.getAttribute("src") || "";
        if (src) {
            const measurementId = src.match(gtagReg) ? src.match(gtagReg)[0] : null;
            return measurementId && brandMeasurementIDs.has(measurementId);
        }
        return false;
    });
    return allBrandScripts;
}

function alreadyLoaded(measurementId: string) {
    const existingScripts = fetchAllExistingScripts();
    existingScripts.forEach((script) => {
        if (script.src.includes(measurementId)) {
            return true;
        }
    });
    return false;
}
/**
 * @param existingScripts is the list of all the brand specific gtag scripts loaded previously
 * @param reservedGoogleId is the measurement id for the brand that is being loaded now
 */
function teardown(existingScripts: (HTMLScriptElement | HTMLIFrameElement)[], reservedGoogleId: string) {
    let hasConflict = false;

    // Get the list of all the brand using google analytics loaded previously ignoring the one that is being loaded now
    const keys = Object.keys(window).filter(
        (key) => key.includes("ga-disable-") && key !== `ga-disable-${reservedGoogleId}`
    );
    // and disable gtag for theirs measurement id
    keys.forEach((key) => {
        window[key] = true;
    });

    // Removing all the previous brand specific gtag scripts from the dom
    existingScripts.forEach((script) => {
        const src = script.getAttribute("src") || "";
        if (src.includes("gtag") && (!src.includes(reservedGoogleId) || !reservedGoogleId)) {
            script.parentNode.removeChild(script);
            hasConflict = true;
        }
    });
    if (hasConflict) {
        resetDataLayer();
    }
}

function setup(measurementId: string, reservedGoogleId: string) {
    const isValidMeasurementId = gtagReg.test(measurementId);
    if (isValidMeasurementId && !alreadyLoaded(measurementId)) {
        setupGTag(measurementId);
    }

    window.dataLayer = window.dataLayer || [];
    if (window.dataLayer.length < 4 || !window.gtag) {
        window.dataLayer.push(function () {
            this.reset();
        });

        window.gtag = function () {
            // eslint-disable-next-line prefer-rest-params
            window.dataLayer.push(arguments);
        };
        window.gtag("js", new Date());
    }

    if (getLastEntry()[0] === "js") {
        window.gtag("config", reservedGoogleId, { groups: [GTagGroup.B2B, GTagGroup.Default] });
    }

    if (isValidMeasurementId && getLastEntry()[0] === "config" && getLastEntry()[1] === reservedGoogleId) {
        window.gtag("config", measurementId, {
            groups: [GTagGroup.Brand, GTagGroup.Default],
            send_page_view: false,
            debug_mode: true,
        });
    }

    if (isValidMeasurementId) {
        // Enabling google tag to send data for this measurement id
        // The value could have been set to true by a previous event
        window["ga-disable-" + measurementId] = false;
    }
}

function setupGTag(googleId: string) {
    const gtagScriptElement = document.createElement("script");
    gtagScriptElement.async = true;
    gtagScriptElement.src = "https://www.googletagmanager.com/gtag/js?id=" + googleId;
    gtagScriptElement.classList.add(GA_BRAND_SCRIPT_CLASS);
    document.head.appendChild(gtagScriptElement);
}

function sameSource(source: IScope) {
    return window.location.href.includes(source?.brand?._id);
}

function fetchTarget(group: GTagGroup) {
    let target = "";
    const { scope } = getStore().getState();
    const googleAnalytics = scope?.brand?.settings?.google_analytics;

    if (group === GTagGroup.Brand && !me.isBuyer()) {
        return target;
    }
    if (group === GTagGroup.B2B && me.isNuOrderUser()) {
        return target;
    }

    switch (group) {
        case GTagGroup.Brand:
            const allowed =
                scope.brand?.hasPreference("google_analytics") &&
                googleAnalytics?.measurement_id &&
                googleAnalytics?.is_enabled;
            target = allowed ? googleAnalytics.measurement_id : "";
            break;
        case GTagGroup.B2B:
            target = fetchB2BMeasurementId();
            break;
        case GTagGroup.Default:
            target = "default";
            break;
        default:
            target = "";
    }

    return target;
}

function resetDataLayer() {
    window.dataLayer.length = 0;
    if (window.google_tag_manager && typeof window.google_tag_manager === "object") {
        for (const gKey of Object.keys(window.google_tag_manager)) {
            if (
                (gtmReg.test(gKey) || gtagReg.test(gKey)) &&
                window.google_tag_manager[gKey].dataLayer &&
                window.google_tag_manager[gKey].dataLayer.reset
            ) {
                window.google_tag_manager[gKey].dataLayer.reset();
            }
        }
    }
}

function getLastEntry(n = 0) {
    return window.dataLayer?.[window.dataLayer.length - (1 + n)] || ["", "", {}];
}

function isDuplicateEvent(current) {
    const last = getLastEntry();
    const secondLast = getLastEntry(1);
    const isSecondLastEmpty =
        _.isEmpty(secondLast?.[0]) ||
        _.isEmpty(secondLast?.[1]) ||
        _.isNil(secondLast?.[2]) ||
        _.isEmpty(secondLast?.[2]);
    const checkSecondLast =
        !isSecondLastEmpty && (secondLast?.[1]?.includes("view_item") || secondLast?.[1]?.includes("page_view"));

    if (current.length !== last.length) {
        return false;
    }

    if (
        current?.[0] !== last?.[0] ||
        (current?.[1] !== last?.[1] && (!checkSecondLast || current?.[1] !== secondLast?.[1]))
    ) {
        return false;
    }

    // Duplicate events can occur when viewing a product from a list/gallery.
    // The only difference between the two events is the affiliation, item_list_id and item_list_name where
    // items in the latter event (current) have undefined values for these fields.
    // Ex. The first view_item event can occur when clicking a product from a catalog (has all defined values).
    // Once the product is opened (after clicking), a second view_item event tends to get triggered (has some undefined values).
    if (
        checkSecondLast &&
        current?.[2]?.items &&
        secondLast?.[2]?.items &&
        current?.[2]?.items?.length === secondLast?.[2]?.items?.length &&
        secondLast?.[2]?.items?.length > 0
    ) {
        // just check if each item brand_id is the same
        return current[2].items.every((item, index) => item?.item_id === secondLast?.[2]?.items?.[index]?.item_id);
    }
    // Duplicate events can occur when page_view gets emitted for a product viewed from a list/gallery that opens its PDP
    if (
        checkSecondLast &&
        current?.[1] === "page_view" &&
        secondLast?.[1] === "page_view" &&
        current?.[2]?.page_title === secondLast?.[2]?.page_title
    ) {
        return true;
    }
    return _.isEqual(current?.[2], last?.[2]);
}

function fetchB2BMeasurementId() {
    return gtm.b2bMeasurementIds?.[window?.NUCONFIG?.ENVIRONMENT] || gtm.b2bMeasurementIds.development;
}
